'use client';

import { useTranslations } from 'next-intl';
import { useMounted } from '@/hooks/useMounted';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import type { PlayerStanding } from '@/draw/types';
import { standingShortName } from '../shortName';
import { SectionLabel } from './SectionLabel';
import { ChartTooltip } from './ChartTooltip';

interface PointsChartProps {
  standings: PlayerStanding[];
  isDark?: boolean;
}

export function PointsChart({ standings, isDark = true }: PointsChartProps) {
  const t = useTranslations('Celebration');
  const mounted = useMounted();

  const data = standings.map((p) => ({
    name: standingShortName(p),
    [t('scored')]: p.gamePointsFor,
    [t('conceded')]: p.gamePointsAgainst,
  }));

  const gridColor = isDark ? 'rgba(255,255,255,0.06)' : '#E8E2D4';
  const tickColor = isDark ? 'rgba(255,255,255,0.4)' : '#1B2D4F80';
  const axisColor = isDark ? 'rgba(255,255,255,0.08)' : '#E8E2D4';
  const scoredColor = isDark ? '#FBBF24' : '#F5A623';
  const concededColor = isDark ? 'rgba(147,161,173,0.4)' : '#1B2D4F30';

  return (
    <>
      <SectionLabel>{t('pointsScoredVsConceded')}</SectionLabel>
      {mounted && (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis
              dataKey="name"
              tick={{ fill: tickColor, fontSize: 12 }}
              axisLine={{ stroke: axisColor }}
            />
            <YAxis tick={{ fill: tickColor, fontSize: 11 }} axisLine={{ stroke: axisColor }} />
            <Tooltip content={<ChartTooltip isDark={isDark} />} />
            <Bar dataKey={t('scored')} fill={scoredColor} radius={[4, 4, 0, 0]} />
            <Bar dataKey={t('conceded')} fill={concededColor} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </>
  );
}
